import type { VisiblePass } from './useFilterStore';
import { parseUtcEpochMs } from './useDateTimeSettings';
import { getTimingBucketIndexForHours } from './useTimingColorScale';

export const ORORATECH_CONSTELLATION_NAME = 'OroraTech Constellation';

/**
 * Constellation membership, matched by name pattern rather than enumeration.
 *
 * This was a hardcoded list of FOREST-4..11, and it silently stopped matching as
 * the fleet grew: FOREST-2/3, FOREST-12..19 and GENA-OT were all absent, so the
 * swath view never requested their scheduled footprints and the predicted vs
 * scheduled filter treated them as third-party satellites. These are our own
 * satellites and their names follow a fixed pattern, so match the pattern — the
 * roster from GET /satellites can then grow without another edit here.
 */
const ORORATECH_NAME_PATTERN = /^(FOREST-\d+|GENA-OT)$/;

const DEFAULT_GROUPING_GAP_MS = 20 * 60 * 1000;

export interface GroupedVisiblePass extends VisiblePass {
  sourcePassIds: string[];
  sourceSatelliteNames: string[];
  sourcePasses: VisiblePass[];
  grouped: boolean;
}

export interface ConstellationGroupingOptions {
  timeBucketReferenceMs?: number;
  groupingGapMs?: number;
}

function toStartMs(pass: VisiblePass): number {
  const ms = parseUtcEpochMs(pass.start_time);
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

function toEndMs(pass: VisiblePass): number {
  const ms = parseUtcEpochMs(pass.end_time);
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

function uniqueValues(values: string[]): string[] {
  return [...new Set(values)];
}

function resolveTimeBucketReferenceMs(
  passes: VisiblePass[],
  providedReferenceMs?: number,
): number {
  if (typeof providedReferenceMs === 'number' && Number.isFinite(providedReferenceMs)) {
    return providedReferenceMs;
  }
  for (const pass of passes) {
    const startMs = toStartMs(pass);
    if (Number.isFinite(startMs)) return startMs;
  }
  return Date.now();
}

function getPassTimingBucket(pass: VisiblePass, referenceMs: number): number {
  const startMs = toStartMs(pass);
  if (!Number.isFinite(startMs)) return getTimingBucketIndexForHours(Number.POSITIVE_INFINITY);
  const hoursToPass = Math.max(0, (startMs - referenceMs) / 3_600_000);
  return getTimingBucketIndexForHours(hoursToPass);
}

export function isOroraTechConstellationSatellite(name: string): boolean {
  return ORORATECH_NAME_PATTERN.test(String(name ?? '').trim().toUpperCase());
}

export function getSatelliteDisplayName(name: string, expanded: boolean): string {
  if (!expanded && isOroraTechConstellationSatellite(name)) {
    return ORORATECH_CONSTELLATION_NAME;
  }
  return name;
}

function toSingleDisplayPass(pass: VisiblePass, expanded: boolean): GroupedVisiblePass {
  const displayName = getSatelliteDisplayName(pass.name, expanded);
  return {
    ...pass,
    name: displayName,
    constellation: displayName === ORORATECH_CONSTELLATION_NAME
      ? ORORATECH_CONSTELLATION_NAME
      : pass.constellation,
    sourcePassIds: [pass.id],
    sourceSatelliteNames: [pass.name],
    sourcePasses: [{ ...pass }],
    grouped: false,
  };
}

function buildGroupedConstellationPass(cluster: VisiblePass[]): GroupedVisiblePass {
  const sorted = [...cluster].sort((a, b) => toStartMs(a) - toStartMs(b));
  const primary = sorted[0];
  const startMs = Math.min(...sorted.map(toStartMs).filter((v) => Number.isFinite(v)));
  const endMs = Math.max(...sorted.map(toEndMs).filter((v) => Number.isFinite(v)));
  const startIso = Number.isFinite(startMs) ? new Date(startMs).toISOString() : primary.start_time;
  const endIso = Number.isFinite(endMs) ? new Date(endMs).toISOString() : primary.end_time;
  const sourcePassIds = sorted.map((pass) => pass.id);
  const sourceSatelliteNames = uniqueValues(sorted.map((pass) => pass.name));
  const sourcePasses = sorted.map((pass) => ({ ...pass }));
  const sensorType = uniqueValues(
    sorted
      .map((pass) => pass.sensor_type)
      .filter((value): value is string => typeof value === 'string' && value.length > 0),
  );
  const spatialValues = uniqueValues(
    sorted
      .map((pass) => pass.spatial_res_m)
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      .map((value) => String(value)),
  );
  const daytimeValues = uniqueValues(
    sorted
      .map((pass) => pass.is_daytime)
      .filter((value): value is boolean => typeof value === 'boolean')
      .map((value) => String(value)),
  );
  const cloudValues = uniqueValues(
    sorted
      .map((pass) => pass.cloud_cover_pct)
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      .map((value) => String(value)),
  );
  const dataSources = uniqueValues(
    sorted
      .map((pass) => pass.data_source)
      .filter((value): value is string => typeof value === 'string' && value.length > 0),
  );
  const directions = [...new Set(
    sorted
      .map((pass) => pass.direction)
      .filter((value): value is NonNullable<VisiblePass['direction']> => value != null),
  )];

  return {
    ...primary,
    id: `orora-${startIso}-${endIso}-${sourcePassIds.length}`,
    name: ORORATECH_CONSTELLATION_NAME,
    start_time: startIso,
    end_time: endIso,
    sensor_type: sensorType.length === 1 ? sensorType[0] : primary.sensor_type,
    spatial_res_m: spatialValues.length === 1 ? Number(spatialValues[0]) : primary.spatial_res_m,
    constellation: ORORATECH_CONSTELLATION_NAME,
    direction: directions.length === 1 ? directions[0] : undefined,
    is_daytime: daytimeValues.length === 1
      ? daytimeValues[0] === 'true'
      : primary.is_daytime,
    data_source: dataSources.length === 1 ? dataSources[0] : primary.data_source,
    cloud_cover_pct: cloudValues.length > 0
      ? Math.round(cloudValues.reduce((sum, v) => sum + Number(v), 0) / cloudValues.length)
      : primary.cloud_cover_pct,
    sourcePassIds,
    sourceSatelliteNames,
    sourcePasses,
    grouped: sourcePassIds.length > 1,
  };
}

export function collapseOroraConstellationPasses(
  passes: VisiblePass[],
  expanded: boolean,
  options?: ConstellationGroupingOptions,
): GroupedVisiblePass[] {
  if (!passes.length) return [];

  const sortedByStart = [...passes].sort((a, b) => toStartMs(a) - toStartMs(b));
  if (expanded) {
    return sortedByStart.map((pass) => toSingleDisplayPass(pass, true));
  }

  const nonConstellation = sortedByStart.filter(
    (pass) => !isOroraTechConstellationSatellite(pass.name),
  );
  const constellation = sortedByStart.filter((pass) => isOroraTechConstellationSatellite(pass.name));

  const groupedConstellation: GroupedVisiblePass[] = [];
  const timeBucketReferenceMs = resolveTimeBucketReferenceMs(
    sortedByStart,
    options?.timeBucketReferenceMs,
  );
  const groupingGapMs = options?.groupingGapMs ?? DEFAULT_GROUPING_GAP_MS;
  let cluster: VisiblePass[] = [];
  let clusterEndMs = Number.NEGATIVE_INFINITY;
  let clusterBucket = -1;

  const flushCluster = () => {
    if (!cluster.length) return;
    if (cluster.length === 1) {
      groupedConstellation.push(toSingleDisplayPass(cluster[0], false));
    } else {
      groupedConstellation.push(buildGroupedConstellationPass(cluster));
    }
    cluster = [];
    clusterEndMs = Number.NEGATIVE_INFINITY;
    clusterBucket = -1;
  };

  for (const pass of constellation) {
    const startMs = toStartMs(pass);
    const endMs = toEndMs(pass);
    const passBucket = getPassTimingBucket(pass, timeBucketReferenceMs);
    if (!cluster.length) {
      cluster = [pass];
      clusterEndMs = Number.isFinite(endMs) ? endMs : startMs;
      clusterBucket = passBucket;
      continue;
    }
    const canMerge = Number.isFinite(startMs)
      && Number.isFinite(clusterEndMs)
      && startMs <= clusterEndMs + groupingGapMs
      && passBucket === clusterBucket;
    if (canMerge) {
      cluster.push(pass);
      if (Number.isFinite(endMs)) clusterEndMs = Math.max(clusterEndMs, endMs);
    } else {
      flushCluster();
      cluster = [pass];
      clusterEndMs = Number.isFinite(endMs) ? endMs : startMs;
      clusterBucket = passBucket;
    }
  }
  flushCluster();

  const nonConstellationDisplay = nonConstellation.map((pass) => toSingleDisplayPass(pass, false));
  return [...nonConstellationDisplay, ...groupedConstellation].sort(
    (a, b) => toStartMs(a) - toStartMs(b),
  );
}

export function groupedPassContainsRawId(pass: GroupedVisiblePass, rawId: string | null | undefined): boolean {
  if (!rawId) return false;
  if (pass.sourcePassIds.length === 0) return pass.id === rawId;
  return pass.sourcePassIds.includes(rawId);
}

export function getPrimaryPassId(pass: GroupedVisiblePass): string {
  return pass.sourcePassIds[0] ?? pass.id;
}
